/**
 * Browser GA4 context used by the signup API. Returns nulls when gtag is not
 * available (blocked, not configured, or consent denied) so signup never
 * depends on analytics availability.
 */

export const GA_MEASUREMENT_ID = 'G-VW2S0SK06N';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const GA_GET_TIMEOUT_MS = 1_000;

type GaValue = string | number | null;

const getGaValue = (field: 'client_id' | 'session_id'): Promise<GaValue> =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      resolve(null);
      return;
    }

    let settled = false;
    const timer = window.setTimeout(() => finish(null), GA_GET_TIMEOUT_MS);
    const finish = (value: GaValue) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(value ?? null);
    };

    try {
      window.gtag('get', GA_MEASUREMENT_ID, field, (value: unknown) => {
        if (typeof value === 'string' || typeof value === 'number') {
          finish(value);
          return;
        }
        finish(null);
      });
    } catch {
      finish(null);
    }
  });

export interface GaContext {
  clientId: string | null;
  sessionId: number | null;
}

export const getGaContext = async (): Promise<GaContext> => {
  const [clientId, sessionId] = await Promise.all([
    getGaValue('client_id'),
    getGaValue('session_id'),
  ]);

  return {
    clientId: typeof clientId === 'string' && clientId.trim() ? clientId : null,
    sessionId: (() => {
      const numeric = Number(sessionId);
      return Number.isFinite(numeric) && numeric > 0
        ? Math.trunc(numeric)
        : null;
    })(),
  };
};
